import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotificationCenter } from '../../context/NotificationContext';
import {
  HiChatAlt2,
  HiSwitchHorizontal,
  HiCurrencyDollar,
  HiClipboardList,
  HiCheck,
  HiBell,
} from 'react-icons/hi';
import { formatDistanceToNow } from 'date-fns';

const TYPE_CONFIG = {
  message: { icon: HiChatAlt2, label: 'Messages', color: 'text-canopy' },
  swap: { icon: HiSwitchHorizontal, label: 'Swap Requests', color: 'text-sunbeam-600' },
  expense: { icon: HiCurrencyDollar, label: 'Expenses', color: 'text-meadow' },
  activity: { icon: HiClipboardList, label: 'Upcoming Activities', color: 'text-blush' },
};

const MAX_PER_GROUP = 5;

const NotificationPanel = ({ onClose }) => {
  const { notifications, unreadCount, markAllRead, markOneRead } = useNotificationCenter();
  const navigate = useNavigate();

  // Group notifications by type
  const grouped = notifications.reduce((acc, item) => {
    if (!acc[item.type]) acc[item.type] = [];
    acc[item.type].push(item);
    return acc;
  }, {});

  const typeOrder = ['message', 'swap', 'expense', 'activity'];

  const handleClick = (notification) => {
    if (notification.isUnread) {
      markOneRead(notification.id);
    }
    navigate(notification.navigateTo);
    onClose();
  };

  const handleMarkAllRead = async () => {
    await markAllRead();
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
    return formatDistanceToNow(date, { addSuffix: true });
  };

  return (
    <div className="absolute right-0 top-full mt-2 w-[calc(100vw-2rem)] sm:w-80 max-h-[28rem] overflow-y-auto card shadow-warm-lg z-50 bg-white">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-surface-100">
        <h3 className="text-sm font-semibold text-ink-800">Notifications</h3>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllRead}
            className="flex items-center gap-1 text-xs font-medium text-canopy hover:text-canopy/80 transition-colors"
          >
            <HiCheck className="text-sm" />
            Mark all read
          </button>
        )}
      </div>

      {/* Empty state */}
      {notifications.length === 0 && (
        <div className="flex flex-col items-center justify-center py-10 px-4">
          <HiBell className="text-3xl text-fog mb-2" />
          <p className="text-sm text-ink-400 font-medium">You're all caught up!</p>
        </div>
      )}

      {/* Grouped notifications */}
      <div className="py-1">
        {typeOrder.map(type => {
          const items = grouped[type];
          if (!items || items.length === 0) return null;
          const config = TYPE_CONFIG[type];
          const Icon = config.icon;
          const visibleItems = items.slice(0, MAX_PER_GROUP);
          const hasMore = items.length > MAX_PER_GROUP;

          return (
            <div key={type}>
              {/* Section header */}
              <div className="flex items-center gap-2 px-4 py-2 mt-1">
                <Icon className={`text-sm ${config.color}`} />
                <span className="text-xs font-semibold text-ink-500 uppercase tracking-wide">
                  {config.label}
                </span>
                <span className="text-xs text-fog font-medium">
                  {items.length}
                </span>
              </div>

              {/* Items */}
              {visibleItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => handleClick(item)}
                  className={`w-full text-left px-4 py-2.5 hover:bg-surface-50 transition-colors flex items-start gap-3 ${
                    item.isUnread ? 'bg-accent-50/50' : ''
                  }`}
                >
                  {item.isUnread && (
                    <span className="w-2 h-2 rounded-full bg-canopy mt-1.5 shrink-0" />
                  )}
                  <div className={`flex-1 min-w-0 ${!item.isUnread ? 'pl-5' : ''}`}>
                    <p className="text-[0.8125rem] font-medium text-ink-800 truncate">
                      {item.title}
                    </p>
                    <p className="text-xs text-ink-400 truncate">{item.body}</p>
                    {item.timestamp && (
                      <p className="text-[0.6875rem] text-fog mt-0.5">
                        {formatTime(item.timestamp)}
                      </p>
                    )}
                  </div>
                </button>
              ))}

              {/* View all link */}
              {hasMore && (
                <button
                  onClick={() => {
                    navigate(visibleItems[0].navigateTo);
                    onClose();
                  }}
                  className="w-full text-left px-4 py-2 text-xs font-medium text-canopy hover:text-canopy/80 pl-9"
                >
                  View all {items.length} {config.label.toLowerCase()}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default NotificationPanel;
